// install (please make sure versions match peerDependencies)
// yarn add @nivo/core @nivo/bar
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ResponsiveBar } from "@nivo/bar";
import { formatDashboardStatus } from "../../utils/salesPayloadFormatter";
import { formatNumber } from "../../utils/dataTransformer";
import { useState } from "react";

// make sure parent container have a defined height when using
// responsive component, otherwise height will be 0 and
// no chart will be rendered.
// website examples showcase many properties,
// you'll often use just a few of them.

const tooltipBackgroundColors = {
  draft_paid: "#9FA8DA",
  wait_customer_accept_paid: "#1565C0",
  wait_payment_when_receive_paid: "#FFCA28",
  wait_payment_cod_paid: "#FB8C00",
  partially_paid_paid: "#CDDC39",
  fully_paid_paid: "#7CB342",
  finished_paid: "#33691E",
  cancelled_paid: "#DADADA",
};

const unfocusedTooltipbackgroundColors = {
  draft_paid: "#CBCFEB",
  wait_customer_accept_paid: "#ADD3FF",
  wait_payment_when_receive_paid: "#FDEEBF",
  wait_payment_cod_paid: "#FFCA87",
  partially_paid_paid: "#E7EEA6",
  fully_paid_paid: "#C6DFAC",
  finished_paid: "#9FC590",
  cancelled_paid: "#EEEDED",
};

const statusList = [
  "draft_paid",
  "wait_customer_accept_paid",
  "wait_payment_when_receive_paid",
  "wait_payment_cod_paid",
  "partially_paid_paid",
  "fully_paid_paid",
  "finished_paid",
  "cancelled_paid",
];

const SOPaidBarChart = ({ data, bottomAxisLabel, maxTotalPaid }) => {
  const [currentStatus, setCurrentStatus] = useState(null);
  const [palette, setPalette] = useState(
    Object.values(tooltipBackgroundColors)
  );
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const shouldShrinkLabel = data?.length >= 18 || isSmallScreen;

  const digits = maxTotalPaid.toString().split(".")[0].length;
  const maxDigit = Math.pow(10, digits - 1);
  const roundedTotalPaid =
    Math.floor(maxTotalPaid / maxDigit) * maxDigit + maxDigit;

  const onMouseEnterHandler = (id) => {
    setCurrentStatus(id);
    const updatedPalette = {
      ...unfocusedTooltipbackgroundColors,
      [id]: tooltipBackgroundColors[id],
    };
    setPalette(Object.values(updatedPalette));
  };

  const onMouseLeaveHandler = () => {
    setCurrentStatus(null);
    setPalette(Object.values(tooltipBackgroundColors));
  };

  return (
    <Box
      sx={{
        height: isSmallScreen ? 525 : 450,
        border: "1px solid #BEBEBE",
        borderRadius: "15px",
        mt: 4,
      }}
    >
      <ResponsiveBar
        data={data}
        keys={[
          "draft_paid",
          "wait_customer_accept_paid",
          "wait_payment_when_receive_paid",
          "wait_payment_cod_paid",
          "partially_paid_paid",
          "fully_paid_paid",
          "finished_paid",
          "cancelled_paid",
        ]}
        indexBy="created_date"
        margin={{
          top: isSmallScreen ? 250 : 100,
          right: 50,
          bottom: shouldShrinkLabel ? 100 : 50,
          left: 120,
        }}
        padding={0.3}
        theme={{ fontFamily: ["Kanit", "sans-serif"].join(", ") }}
        valueScale={{
          type: "linear",
          min: 0,
          max: roundedTotalPaid >= 100 ? roundedTotalPaid : undefined,
        }}
        indexScale={{ type: "band", round: true }}
        onMouseEnter={({ id }) => onMouseEnterHandler(id)}
        onMouseLeave={onMouseLeaveHandler}
        colors={palette}
        defs={[
          {
            id: "dots",
            type: "patternDots",
            background: "inherit",
            color: "#38bcb2",
            size: 4,
            padding: 1,
            stagger: true,
          },
          {
            id: "lines",
            type: "patternLines",
            background: "inherit",
            color: "#eed312",
            rotation: -45,
            lineWidth: 6,
            spacing: 10,
          },
        ]}
        borderColor={{
          from: "color",
          modifiers: [["darker", 1.6]],
        }}
        tooltip={({ data, id, value }) => {
          return (
            <Box
              sx={{
                boxShadow: "0px 2px 10px #E5E5E5",
                borderRadius: "5px",
                overflow: "hidden",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  color: "#EFF2FF",
                  backgroundColor: tooltipBackgroundColors[id],
                  p: 1,
                }}
              >{`สถานะ ${formatDashboardStatus(id)}`}</Box>
              <Box
                sx={{
                  display: "flex",
                  backgroundColor: "#FFFFFF",
                  color: "#333333",
                  p: 1,
                }}
              >
                <Typography
                  fontWeight={400}
                >{`${data.created_date}:`}</Typography>
                <Typography sx={{ ml: 0.5 }} fontWeight={600}>{`${formatNumber(
                  value
                )} บาท`}</Typography>
              </Box>
            </Box>
          );
        }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 5,
          tickPadding: 5,
          tickRotation: shouldShrinkLabel ? -90 : 0,
          legend: bottomAxisLabel,
          legendPosition: "middle",
          legendOffset: shouldShrinkLabel ? 80 : 32,
        }}
        axisLeft={{
          tickSize: 10,
          tickPadding: 5,
          tickRotation: 0,
          legend: "บาท",
          legendPosition: "middle",
          legendOffset: -90,
          format: (value) => (value !== 0 ? formatNumber(value) : 0),
        }}
        layers={[
          "grid",
          "axes",
          "markers",
          "bars",
          "legends",
          "annotations",
          BarTotalsLayer({ currentStatus }),
        ]}
        labelFormat={(data) => <tspan y={0}>{data}</tspan>}
        labelSkipWidth={12}
        labelSkipHeight={12}
        labelTextColor={{
          from: "color",
          modifiers: [["darker", 1.6]],
        }}
        legendLabel={(datum) => {
          return formatDashboardStatus(datum.id);
        }}
        legends={[
          {
            dataFrom: "keys",
            anchor: isSmallScreen ? "top-left" : "top",
            direction: isSmallScreen ? "column" : "row",
            justify: false,
            translateX: isSmallScreen ? -50 : 0,
            translateY: isSmallScreen ? -225 : -50,
            itemsSpacing: 2,
            itemWidth: 120,
            itemHeight: 20,
            itemDirection: "left-to-right",
            itemOpacity: 0.85,
            symbolSize: 20,
            effects: [
              {
                on: "hover",
                style: {
                  itemOpacity: 1,
                },
              },
            ],
          },
        ]}
        role="application"
        ariaLabel="Sales Order Confirmed Status Paid Chart"
        enableLabel={false}
      />
    </Box>
  );
};

const BarTotalsLayer =
  ({ currentStatus }) =>
  (props) => {
    const labelOffset = 10;
    const labelFontSize = 12;
    if (props.bars.length === 0) return null;
    // compute totals for each index/bar
    const totals = {};
    const totalHeight = {};
    const bandwidth = props.bars[0].width;
    let currentStatusIndex;
    if (currentStatus) {
      currentStatusIndex = statusList.indexOf(currentStatus);
    }
    props.bars.forEach((bar) => {
      const indexValue = bar.data.indexValue;
      if (!(indexValue in totals)) {
        totals[indexValue] = 0;
        totalHeight[indexValue] = 0;
      }
      if (currentStatus && !bar.data.hidden && currentStatus === bar.data.id) {
        totals[indexValue] += bar.data.value;
        totalHeight[indexValue] += bar.data.value;
      } else if (
        !bar.data.hidden &&
        statusList.indexOf(bar.data.id) <= currentStatusIndex &&
        statusList.indexOf(bar.data.id) !== -1
      ) {
        totalHeight[indexValue] += bar.data.value;
      }
      if (!currentStatus && !bar.data.hidden) {
        totals[indexValue] += bar.data.value;
        totalHeight[indexValue] += bar.data.value;
      }
    });
    // place text elements above the bars
    const labels = Object.keys(totalHeight).map((indexValue) => {
      const x = props.xScale(indexValue) + bandwidth / 2;
      const y = props.yScale(totalHeight[indexValue]) - labelOffset;
      return (
        <text
          key={"total." + indexValue}
          x={x}
          y={y}
          textAnchor={"middle"}
          fontSize={labelFontSize}
        >
          {totals[indexValue] > 0 ? formatNumber(totals[indexValue]) : ""}
        </text>
      );
    });
    return <>{labels}</>;
  };

export default SOPaidBarChart;
